/**
 * Tenant Service
 * Handles fetching and managing tenant data from Firebase
 */

import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from 'foodhub-shared/firebase';
import type { Tenant, Subscription } from 'foodhub-shared/types';

const TENANTS_COLLECTION = 'tenants';
const SUBSCRIPTIONS_COLLECTION = 'subscriptions';
const PAYMENTS_COLLECTION = 'payments';

/**
 * Get all tenants
 */
export const getAllTenants = async (): Promise<Tenant[]> => {
  try {
    const q = query(collection(db, TENANTS_COLLECTION));
    const querySnapshot = await getDocs(q);
    
    const tenants: Tenant[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      tenants.push({
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
        trialEndsAt: data.trialEndsAt?.toDate?.(),
        subscriptionEndsAt: data.subscriptionEndsAt?.toDate?.(),
      } as Tenant);
    });

    return tenants;
  } catch (error: any) {
    console.error('Get all tenants error:', error);
    throw new Error(error.message || 'Failed to fetch tenants');
  }
};

/**
 * Get active tenants count
 */
export const getActiveTenantCount = async (): Promise<number> => {
  try {
    const q = query(
      collection(db, TENANTS_COLLECTION),
      where('status', '==', 'active')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error: any) {
    console.error('Get active tenant count error:', error);
    return 0;
  }
};

/**
 * Get active subscriptions count
 */
export const getActiveSubscriptionCount = async (): Promise<number> => {
  try {
    const q = query(
      collection(db, SUBSCRIPTIONS_COLLECTION),
      where('status', '==', 'active')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error: any) {
    console.error('Get active subscription count error:', error);
    return 0;
  }
};

/**
 * Get monthly revenue (sum of active subscription amounts)
 */
export const getMonthlyRevenue = async (): Promise<number> => {
  try {
    const q = query(
      collection(db, SUBSCRIPTIONS_COLLECTION),
      where('status', '==', 'active')
    );
    const querySnapshot = await getDocs(q);

    let totalRevenue = 0;
    for (const docSnapshot of querySnapshot.docs) {
      const subscription = docSnapshot.data() as Subscription;
      const tenantId = subscription.tenantId;

      // Get tenant to get subscription plan price
      const tenantDocRef = doc(db, TENANTS_COLLECTION, tenantId);
      const tenantDocSnap = await getDoc(tenantDocRef);
      if (tenantDocSnap.exists()) {
        const tenant = tenantDocSnap.data() as Tenant;
        // Estimate monthly revenue based on plan
        const monthlyAmount = estimateMonthlyAmount(tenant.subscriptionPlan);
        totalRevenue += monthlyAmount;
      }
    }

    return totalRevenue;
  } catch (error: any) {
    console.error('Get monthly revenue error:', error);
    return 0;
  }
};

/**
 * Get pending payments count
 */
export const getPendingPaymentCount = async (): Promise<number> => {
  try {
    const q = query(
      collection(db, PAYMENTS_COLLECTION),
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error: any) {
    console.error('Get pending payment count error:', error);
    return 0;
  }
};

/**
 * Subscribe to all tenants (real-time)
 */
export const subscribeToTenants = (
  callback: (tenants: Tenant[]) => void
): (() => void) => {
  const q = query(collection(db, TENANTS_COLLECTION));
  
  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const tenants: Tenant[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      tenants.push({
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
        trialEndsAt: data.trialEndsAt?.toDate?.(),
        subscriptionEndsAt: data.subscriptionEndsAt?.toDate?.(),
      } as Tenant);
    });
    callback(tenants);
  });

  return unsubscribe;
};

/**
 * Get tenant by ID
 */
export const getTenantById = async (tenantId: string): Promise<Tenant | null> => {
  try {
    const docRef = doc(db, TENANTS_COLLECTION, tenantId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date(),
      trialEndsAt: data.trialEndsAt?.toDate?.(),
      subscriptionEndsAt: data.subscriptionEndsAt?.toDate?.(),
    } as Tenant;
  } catch (error: any) {
    console.error('Get tenant by ID error:', error);
    return null;
  }
};

/**
 * Get subscriptions for a tenant
 */
export const getTenantSubscriptions = async (tenantId: string): Promise<Subscription[]> => {
  try {
    const q = query(
      collection(db, SUBSCRIPTIONS_COLLECTION),
      where('tenantId', '==', tenantId)
    );
    const querySnapshot = await getDocs(q);
    
    const subscriptions: Subscription[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      subscriptions.push({
        ...data,
        id: doc.id,
        startDate: data.startDate?.toDate?.() || new Date(),
        endDate: data.endDate?.toDate?.() || new Date(),
        trialEndsAt: data.trialEndsAt?.toDate?.(),
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      } as Subscription);
    });

    return subscriptions;
  } catch (error: any) {
    console.error('Get tenant subscriptions error:', error);
    return [];
  }
};

/**
 * Estimate monthly amount based on subscription plan
 */
const estimateMonthlyAmount = (plan: string): number => {
  const planPrices: Record<string, number> = {
    basic: 99,
    pro: 299,
    enterprise: 999,
  };
  return planPrices[plan] || 0;
};

export default {
  getAllTenants,
  getActiveTenantCount,
  getActiveSubscriptionCount,
  getMonthlyRevenue,
  getPendingPaymentCount,
  subscribeToTenants,
  getTenantById,
  getTenantSubscriptions,
};

